import React, { useEffect, useMemo, useRef, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import { rebalanceFitWeights } from './fitWeights';
import type { BrokerSettings, BrokerSettingsPreview, BrokerFactorImpact, BrokerFitComponentImpact, BrokerRecommendKnobImpact, BrokerCohortBands, BrokerCutoffSolution, BrokerBuyerSearchResult, BrokerParentSearchResult, BrokerFilterBucket, BrokerContactOverride } from '../../api/types';
import { setBrokerThresholds } from '../broker-view/brokerViewTokens';
import './BrokerSettingsSection.css';

/**
 * Admin-panel section for the shared, admin-tunable Broker View settings:
 * a managed buyer-exclusions list + the calculation thresholds (urgency, price
 * position, vehicle-filter splits, and the fit-score blend). Editable by broker
 * -role users + admins. Saving buyer-exclusion or fit changes recomputes the
 * buyer-match set in the background (a brief "recalculating" state).
 */
export const BrokerSettingsSection: React.FC = () => {
  const [draft, setDraft] = useState<BrokerSettings | null>(null);
  const [cohortBands, setCohortBands] = useState<BrokerCohortBands | null>(null);
  const [matchTypes, setMatchTypes] = useState<{ tier: number; name: string; definition: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // §1.1 live preview of what the unsaved draft would do to the board.
  const [preview, setPreview] = useState<BrokerSettingsPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [baseline, setBaseline] = useState<string | null>(null);
  // §1.1.3 what each control is doing to the live board. Loaded once; it describes
  // the SAVED settings, so it is not refetched while the draft is being edited.
  const [impact, setImpact] = useState<Record<string, BrokerFactorImpact>>({});
  const [impactTotal, setImpactTotal] = useState(0);
  const [fitImpact, setFitImpact] = useState<Record<string, BrokerFitComponentImpact>>({});
  const [knobImpact, setKnobImpact] = useState<BrokerRecommendKnobImpact[]>([]);

  const toPatch = (s: BrokerSettings) => {
    const { version, updated_at, updated_by, ...patch } = s as any;
    void version; void updated_at; void updated_by;
    return patch as Partial<BrokerSettings>;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await siftApiClient.getBrokerSettings();
        if (cancelled) return;
        setDraft(resp.settings);
        setBaseline(JSON.stringify(toPatch(resp.settings)));
        setCohortBands(resp.cohort_bands);
        setMatchTypes(resp.match_types || []);
        try {
          const fi = await siftApiClient.getBrokerFactorImpact();
          if (!cancelled && fi.available) {
            setImpact(Object.fromEntries(fi.factors.map((f) => [f.key, f])));
            setImpactTotal(fi.total);
          }
        } catch {
          // Additive: the controls still work without their referent.
        }
        try {
          const ff = await siftApiClient.getBrokerFitImpact();
          if (!cancelled && ff.available) {
            setFitImpact(Object.fromEntries(ff.components.map((c) => [c.key, c])));
          }
        } catch {
          // Same: ranking works without the measured influence beside it.
        }
        try {
          const rk = await siftApiClient.getBrokerRecommendImpact();
          if (!cancelled && rk.available) setKnobImpact(rk.knobs);
        } catch {
          // Same again: the knobs still work without their measured effect.
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Failed to load broker settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced dry run. Only fires while the draft differs from what is saved, so
  // opening the panel costs nothing and a reverted edit clears rather than
  // re-queries. The abort controller keeps a slow earlier response from landing
  // on top of a newer one.
  const dirty = !!draft && baseline !== null && JSON.stringify(toPatch(draft)) !== baseline;
  useEffect(() => {
    if (!draft || !dirty) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const ctl = new AbortController();
    const id = window.setTimeout(async () => {
      setPreviewing(true);
      try {
        const res = await siftApiClient.previewBrokerSettings(toPatch(draft), ctl.signal);
        if (!ctl.signal.aborted) { setPreview(res); setPreviewError(null); }
      } catch (err: any) {
        if (ctl.signal.aborted) return;
        setPreview(null);
        setPreviewError(err?.response?.data?.detail || 'Preview unavailable');
      } finally {
        if (!ctl.signal.aborted) setPreviewing(false);
      }
    }, 500);
    return () => { window.clearTimeout(id); ctl.abort(); };
  }, [draft, dirty]);

  // Generic nested-field setter: mutates a deep clone at the given path.
  const setPath = (path: (string | number)[], value: unknown) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next: any = structuredClone(prev);
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!weightsValid) {
      setError(`Fit component weights must sum to 1.00 (currently ${weightSum.toFixed(2)}).`);
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const { version, updated_at, updated_by, ...patch } = draft;
      void version; void updated_at; void updated_by;
      const res = await siftApiClient.updateBrokerSettings(patch);
      setDraft(res.settings);
      // Re-baselining clears the preview via the dirty check.
      setBaseline(JSON.stringify(toPatch(res.settings)));
      // Push the saved config straight into the shared token state: the board is
      // still mounted behind this panel and would otherwise keep scoring against
      // the settings it loaded when it opened.
      setBrokerThresholds(res.settings);
      setNotice(res.recompute
        ? 'Saved. Recalculating recommendations in the background — the board will refresh once it completes (usually under a minute).'
        : 'Saved. The board has been updated.');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bss-state glass-text-secondary">Loading broker settings…</div>;
  if (!draft) return <div className="bss-state bss-error">{error || 'Broker settings unavailable'}</div>;

  const u = draft.urgency;
  const pb = draft.price_bands;
  const fl = draft.filters;
  const fit = draft.fit;
  const knobBy: Record<string, BrokerRecommendKnobImpact> =
    Object.fromEntries(knobImpact.map((k) => [k.key, k]));
  const weightSum = fit.weights.match + fit.weights.prox + fit.weights.strength
    + fit.weights.retention + fit.weights.recency + fit.weights.engage;
  const weightsValid = Math.abs(weightSum - 1) <= 0.001;

  return (
    <div className="broker-settings-section">
      <p className="glass-text-secondary bss-intro">
        Shared across all broker users. Buyer-exclusion and fit-score changes trigger a background
        recalculation of recommendations; everything else applies on the next board load.
      </p>

      {error && <div className="admin-error">{error}<button onClick={() => setError(null)}>✕</button></div>}
      {notice && <div className="bss-notice">{notice}<button onClick={() => setNotice(null)}>✕</button></div>}

      {/* Buyer exclusions */}
      <BuyerExclusions
        exclusions={draft.buyer_exclusions}
        onChange={(next) => setPath(['buyer_exclusions'], next)}
      />

      {/* §3.2 contact overrides. NOT part of the settings draft — see the note. */}
      <ContactOverrides />

      {/* Urgency */}
      <Section title="Urgency scoring">
        <p className="bss-help">
          Each VIN starts at 0%. Every signal below that <b>fires</b> adds its share of the urgency a
          vehicle can earn — the boosts always total <b>100%</b> — and a penalty subtracts. The result
          is the VIN's urgency percentage, which maps to Critical / High / Medium / Low using the
          cutoffs below. Rank the signals, or type a share directly.
        </p>
        <RankedSignals
          u={u}
          impact={impact}
          impactTotal={impactTotal}
          setPath={setPath}
        />
        <div className="bss-subhead">Tier cutoffs</div>
        <p className="bss-help">
          A VIN lands in the highest tier its percentage clears. Rather than guessing a number, say
          what share of the board each tier should be and solve for it — the score is coarse, so the
          solver reports the share you actually get.
        </p>
        <div className="bss-grid">
          <NumField label="Critical ≥ (%)" value={u.tier_cutoffs.critical} onChange={(v) => setPath(['urgency', 'tier_cutoffs', 'critical'], v)} />
          <NumField label="High ≥ (%)" value={u.tier_cutoffs.high} onChange={(v) => setPath(['urgency', 'tier_cutoffs', 'high'], v)} />
          <NumField label="Medium ≥ (%)" value={u.tier_cutoffs.medium} onChange={(v) => setPath(['urgency', 'tier_cutoffs', 'medium'], v)} />
        </div>
        <CutoffSolver onApply={(tier, cutoff) => setPath(['urgency', 'tier_cutoffs', tier], cutoff)} />

        <div className="bss-subhead">Guards</div>
        <div className="bss-grid">
          <BoolField
            label="Ignore 2nd-chance bids"
            value={!!u.ignore_second_chance_bids}
            hint="When on, the Has-bids signal does not fire for 2nd-chance relist VINs (their bids aren't clean buyer intent)."
            onChange={(v) => setPath(['urgency', 'ignore_second_chance_bids'], v)}
          />
        </div>
      </Section>

      {/* Price position (edges; titles are fixed) */}
      <Section title="Price position" sub="asking ÷ MMR">
        <p className="bss-help">
          Band edges classify each VIN's asking price vs market value (MMR) into Under Market /
          Priced Right / Above Market / Over Market. The “Priced Right” upper edge also defines the
          urgency “priced at/under market” signal.
        </p>
        <div className="bss-grid">
          <NumField label="Under Market <" step={0.01} value={pb.under} onChange={(v) => setPath(['price_bands', 'under'], v)} />
          <NumField label="Priced Right ≤" step={0.01} value={pb.right} onChange={(v) => setPath(['price_bands', 'right'], v)} />
          <NumField label="Above Market ≤ (else Over)" step={0.01} value={pb.high} onChange={(v) => setPath(['price_bands', 'high'], v)} />
        </div>
      </Section>

      {/* Custom filter buckets */}
      <Section title="Filter buckets">
        <p className="bss-help">
          Build your own buckets for each chip filter — add as many as you like, name them, and set each
          range (min / max inclusive; leave blank for “no bound”). Shown on the board in this order.
        </p>
        <CustomBucketEditor title="Days Listed" unit="days" buckets={fl.days_listed} onChange={(next) => setPath(['filters', 'days_listed'], next)} />
        <CustomBucketEditor title="Listings" unit="listings" buckets={fl.listing_occ} onChange={(next) => setPath(['filters', 'listing_occ'], next)} />
        <CustomBucketEditor title="Bids" unit="bids" buckets={fl.total_bids} onChange={(next) => setPath(['filters', 'total_bids'], next)} />
        <CustomBucketEditor title="Repriced" unit="days since reprice" buckets={fl.reprice} onChange={(next) => setPath(['filters', 'reprice'], next)} />
        <CustomBucketEditor title="VDP Views" unit="views" buckets={fl.vdp_views} onChange={(next) => setPath(['filters', 'vdp_views'], next)} />
        <CustomBucketEditor title="Saves" unit="workbook saves" buckets={fl.workbook_saves} onChange={(next) => setPath(['filters', 'workbook_saves'], next)} />
      </Section>

      {/* Fit score (precompute) */}
      <Section title="Fit score blend" sub="recompute on save">
        <p className="bss-help">
          Fit is a 0–100 score for how well a buyer matches a VIN. Rank the components by how much
          they should matter, or set a share directly — the six always total 100%. Components that
          have their own tuning carry it inside their row.
        </p>
        <RankedFitWeights
          fit={fit}
          weights={fit.weights}
          impact={fitImpact}
          onWeights={(w) => setPath(['fit', 'weights'], w)}
          setPath={setPath}
        />
      </Section>

      {/* Recommendation tuning (read-time) */}
      <Section title="Recommendation tuning" sub="applies on next board load">
        <p className="bss-help">
          After the fit score ranks a VIN's buyers, these adjust the <b>top-5 recommendations</b>.
          They do three different jobs — decide who is eligible at all, nudge the order, or override
          it — so they are grouped that way rather than as one list of numbers.
        </p>

        <div className="bss-subhead">
          1. Who can be recommended <span className="bss-legend-sub">drops buyers entirely; the deeper pool backfills</span>
        </div>
        <p className="bss-help">A <b>hard</b> cap that drops recommended buyers farther than the effective distance. <b>Base cap</b> is the distance for a ~$0 vehicle; <b>0</b> = off. <b>Value scale</b> makes it value-aware — effective cap = base × (1 + MMR ÷ scale) — so a pricier unit reaches farther. A buyer with unknown distance is never dropped.</p>
        <div className="bss-grid">
          <NumField label="Base cap (miles, 0 = off)" value={draft.recommend?.max_distance_miles ?? 0} onChange={(v) => setPath(['recommend', 'max_distance_miles'], v)} />
          <NumField label="Value scale ($ of MMR)" value={draft.recommend?.distance_value_scale ?? 15000} onChange={(v) => setPath(['recommend', 'distance_value_scale'], v)} />
        </div>
        <div className="bss-grid">
          <BoolField
            label="Exclude active bidders from recommendations"
            value={!!draft.recommend?.exclude_active_bidders}
            hint="When on, a buyer already actively bidding on a VIN is dropped from that VIN's top-5 (the deeper pool backfills), so outreach doesn't target an active bidder. Off = they stay, marked '● Bidding now'. Either way they still appear in 'Also engaged this VIN'."
            onChange={(v) => setPath(['recommend', 'exclude_active_bidders'], v)}
          />
        </div>

        <div className="bss-subhead">
          2. Nudges to the order
          <span className="bss-legend-sub">
            {knobImpact.length
              ? 'ordered by what each actually does to today\u2019s board'
              : 'bounded adjustments to an already-ranked list'}
          </span>
        </div>
        <p className="bss-help">
          These shift a buyer up or down without removing anyone. <b>The numbers are not comparable
          to each other</b> — two knobs set to the same value can differ enormously in effect — so
          each row states what it measurably does instead.
        </p>
        {NUDGE_KNOBS.slice().sort((a, b) =>
          (knobBy[b.key]?.moves ?? -1) - (knobBy[a.key]?.moves ?? -1),
        ).map((n) => (
          <div key={n.key} className="bss-knob">
            <div className="bss-knob-head">
              <span className="bss-ranked-name">{n.label}</span>
              {knobBy[n.key] && <KnobImpactLine impact={knobBy[n.key]} />}
            </div>
            <p className="bss-help">{n.what}</p>
            {n.controls(draft.recommend || {}, setPath)}
          </div>
        ))}

        <div className="bss-subhead">
          3. Hard overrides <span className="bss-legend-sub">these beat the score outright</span>
        </div>
        <div className="bss-knob">
          <div className="bss-knob-head">
            <span className="bss-ranked-name">Rank engaged buyers first</span>
            {knobBy.engagement_first && <KnobImpactLine impact={knobBy.engagement_first} />}
          </div>
          <div className="bss-grid">
            <BoolField
              label="Rank engaged buyers first"
              value={!!draft.recommend?.engagement_first}
              hint="When on, any buyer who engaged THIS VIN (bid / view / save) sorts strictly ahead of non-engaged cohort matches — so a lost bidder on this exact VIN surfaces at the top regardless of cohort fit. Off = engagement still earns the bounded boost above, but doesn't override a stronger cohort match."
              onChange={(v) => setPath(['recommend', 'engagement_first'], v)}
            />
          </div>
        </div>
        <div className="bss-grid">
          <BoolField
            label="Exclude previous Canadian buyers"
            value={!!draft.recommend?.exclude_canadian}
            hint="When on, buyers whose country (from the DIM_ACCOUNT engagement feed) is Canadian are hard-dropped from every recommendation surface — the per-VIN top-5, the deeper pool, and the Buyer-tab board — with the deeper pool backfilling. Off = they stay, marked with a 🇨🇦 badge. A buyer with unknown country is never dropped."
            onChange={(v) => setPath(['recommend', 'exclude_canadian'], v)}
          />
        </div>
        {matchTypes.length > 0 && (
          <div className="bss-defs">
            <div className="bss-subhead">What the match types mean</div>
            <p className="bss-help">Tiers 1–4 all require the same make + model, adding progressively tighter year / mileage / CR cohort and trim / drivetrain overlap. Segment is the exception: a different make/model in the same body / size class.</p>
            {matchTypes.map((m) => (
              <div key={m.tier} className="bss-def"><b>{m.name}</b> — {m.definition}</div>
            ))}
          </div>
        )}
      </Section>

      {/* Cohort bands (read-only) */}
      {cohortBands && (
        <Section title="Cohort bands" sub="fixed">
          <p className="bss-help">
            The buyer↔VIN matching grain. Locked to the upstream buyer-activity aggregation — not editable.
            {cohortBands.cohort_basis === 'age' && (
              <> Vehicles are cohorted by <b>age</b>, which re-cuts itself every January, not by model year.</>
            )}
          </p>
          <div className="bss-bands">
            <div><b>{cohortBands.cohort_label}</b>: {cohortBands.cohort.join(' · ')}</div>
            <div><b>Mileage</b>: {cohortBands.mileage.join(' · ')}</div>
            <div><b>Condition</b>: {cohortBands.condition.join(' · ')}</div>
          </div>
        </Section>
      )}

      {dirty && (
        <div className="bss-preview" aria-live="polite">
          <div className="bss-preview-head">
            <b>Preview — unsaved changes</b>
            <span className="bss-preview-sub">
              {previewing ? 'Recalculating…' : 'How the board would look if you saved now. Nothing has been saved yet.'}
            </span>
          </div>
          {previewError && <div className="bss-preview-err">{previewError}</div>}
          {preview && !previewError && (
            <div className="bss-preview-grid">
              <PreviewGroup
                title="Urgency"
                keys={['critical', 'high', 'medium', 'low']}
                current={preview.current.urgency as unknown as Record<string, number>}
                candidate={preview.candidate.urgency as unknown as Record<string, number>}
                delta={preview.delta.urgency}
              />
              <PreviewGroup
                title="Price position"
                keys={['under', 'right', 'high', 'over', 'na']}
                labels={{ na: 'n/a' }}
                current={preview.current.price as unknown as Record<string, number>}
                candidate={preview.candidate.price as unknown as Record<string, number>}
                delta={preview.delta.price}
              />
            </div>
          )}
        </div>
      )}

      <div className="bss-actions">
        <button className="glass-button bss-save" onClick={handleSave} disabled={saving || !weightsValid}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {!weightsValid && (
          <span className="bss-sum-bad">Fit component weights must sum to 1.00 (currently {weightSum.toFixed(2)}).</span>
        )}
      </div>
    </div>
  );
};

/** §1.1 preview: one bucket group as current → candidate with a signed delta. */
const PreviewGroup: React.FC<{
  title: string;
  keys: string[];
  labels?: Record<string, string>;
  current: Record<string, number>;
  candidate: Record<string, number>;
  delta: Record<string, number>;
}> = ({ title, keys, labels, current, candidate, delta }) => (
  <div className="bss-preview-group">
    <div className="bss-preview-title">{title}</div>
    <table className="bss-preview-table">
      <thead>
        <tr><th>Bucket</th><th>Now</th><th>After</th><th>Change</th></tr>
      </thead>
      <tbody>
        {keys.map((k) => {
          const d = delta[k] ?? 0;
          return (
            <tr key={k}>
              <td>{labels?.[k] ?? k}</td>
              <td>{(current[k] ?? 0).toLocaleString()}</td>
              <td>{(candidate[k] ?? 0).toLocaleString()}</td>
              <td className={d > 0 ? 'bss-delta-up' : d < 0 ? 'bss-delta-down' : 'bss-delta-flat'}>
                {d === 0 ? '—' : `${d > 0 ? '+' : ''}${d.toLocaleString()}`}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/** A collapsible settings group (native <details> — accessible, no extra state).
 * Replaces the old always-open fieldsets so the panel isn't one giant scroll. */
const Section: React.FC<{ title: string; sub?: string; defaultOpen?: boolean; className?: string; children: React.ReactNode }> = ({ title, sub, defaultOpen, className, children }) => (
  <details className={`bss-group${className ? ` ${className}` : ''}`} open={defaultOpen}>
    <summary className="bss-legend">
      <span className="bss-legend-title">{title}</span>
      {sub && <span className="bss-legend-sub">{sub}</span>}
    </summary>
    <div className="bss-section-body">{children}</div>
  </details>
);

const CustomBucketEditor: React.FC<{
  title: string;
  unit: string;
  buckets: BrokerFilterBucket[];
  onChange: (next: BrokerFilterBucket[]) => void;
}> = ({ title, unit, buckets, onChange }) => {
  const set = (i: number, patch: Partial<BrokerFilterBucket>) =>
    onChange(buckets.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => onChange(buckets.filter((_, idx) => idx !== i));
  const add = () => onChange([...buckets, { label: 'New bucket', min: null, max: null }]);

  return (
    <div className="bss-bucket-editor">
      <div className="bss-bucket-editor-head">
        <span className="bss-filter-name">{title}</span>
        <button type="button" className="bss-bucket-add" onClick={add}>+ Add bucket</button>
      </div>
      {buckets.length === 0 && <div className="bss-help">No buckets — add at least one.</div>}
      {buckets.map((b, i) => (
        <div key={i} className="bss-bucket-row">
          <TextField label="Title" value={b.label} onChange={(v) => set(i, { label: v })} />
          <NullableNumField label={`Min ${unit}`} value={b.min} onChange={(v) => set(i, { min: v })} />
          <NullableNumField label={`Max ${unit}`} value={b.max} onChange={(v) => set(i, { max: v })} />
          <button type="button" className="bss-bucket-remove" onClick={() => remove(i)} aria-label={`Remove bucket ${b.label}`}>✕</button>
        </div>
      ))}
    </div>
  );
};

const NullableNumField: React.FC<{ label: string; value: number | null; onChange: (v: number | null) => void }> = ({ label, value, onChange }) => (
  <label className="bss-field">
    <span className="bss-field-label">{label}</span>
    <input
      type="number"
      className="glass-input bss-num"
      placeholder="—"
      value={value ?? ''}
      onChange={(e) => {
        const raw = e.target.value.trim();
        if (raw === '') { onChange(null); return; }
        const n = parseFloat(raw);
        if (!Number.isNaN(n)) onChange(n);
      }}
    />
  </label>
);

const TextField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <label className="bss-field">
    <span className="bss-field-label">{label}</span>
    <input
      type="text"
      className="glass-input bss-text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
);

const NumField: React.FC<{ label: string; value: number; step?: number; onChange: (v: number) => void }> = ({ label, value, step, onChange }) => (
  <label className="bss-field">
    <span className="bss-field-label">{label}</span>
    <input
      type="number"
      className="glass-input bss-num"
      value={Number.isFinite(value) ? value : 0}
      step={step ?? 1}
      onChange={(e) => {
        const n = parseFloat(e.target.value);
        if (!Number.isNaN(n)) onChange(n);
      }}
    />
  </label>
);

const BoolField: React.FC<{ label: string; value: boolean; hint?: string; onChange: (v: boolean) => void }> = ({ label, value, hint, onChange }) => (
  <label className="bss-field" title={hint}>
    <span className="bss-field-label">{label}</span>
    <input
      type="checkbox"
      className="bss-check"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
    />
  </label>
);

/** A small inline numeric input embedded inside a signal's "fires when" sentence. */
const InlineNum: React.FC<{ value: number; step?: number; onChange: (v: number) => void }> = ({ value, step, onChange }) => (
  <input
    type="number"
    className="glass-input bss-inline-num"
    value={Number.isFinite(value) ? value : 0}
    step={step ?? 1}
    onChange={(e) => { const n = parseFloat(e.target.value); if (!Number.isNaN(n)) onChange(n); }}
  />
);

/** One urgency signal, laid out as "<name> — fires when <condition>" on the left
 * and its point weight on the right, so the threshold + weight read as one rule.
 * Reused for the fit-score components (name + what it measures + its weight). */
/** Says whether a signal pushes a VIN UP or DOWN the board, so direction is read
 * rather than decoded from a minus sign. The signed input stays — this labels it,
 * it does not replace it. */
const DirectionChip: React.FC<{ weight: number }> = ({ weight }) => {
  const w = Number.isFinite(weight) ? weight : 0;
  if (w === 0) return <span className="bss-dir bss-dir-off" title="No effect on the score">Off</span>;
  return w > 0
    ? <span className="bss-dir bss-dir-up" title="Raises urgency — moves the VIN toward Critical">Boost</span>
    : <span className="bss-dir bss-dir-down" title="Lowers urgency — moves the VIN toward Low">Penalty</span>;
};

const SignalRow: React.FC<{ name: string; weight: number; step?: number; onWeight: (v: number) => void; impact?: BrokerFactorImpact; total?: number; children?: React.ReactNode }> = ({ name, weight, step, onWeight, impact, total, children }) => (
  <div className="bss-signal-row">
    <div className="bss-signal-desc">
      <b>{name}</b>{children ? <> — {children}</> : null}
      {impact && <FactorImpactLine impact={impact} total={total ?? 0} />}
    </div>
    <div className="bss-signal-input">
      <DirectionChip weight={weight} />
      <input
        type="number"
        className="glass-input bss-signal-weight"
        value={Number.isFinite(weight) ? weight : 0}
        step={step ?? 1}
        onChange={(e) => { const n = parseFloat(e.target.value); if (!Number.isNaN(n)) onWeight(n); }}
        aria-label={`${name} value`}
      />
    </div>
  </div>
);

/** §1.1.5 the urgency signals, as data rather than 16 hand-written rows.
 *
 * ``when`` is the condition the signal fires on, and carries the inline threshold
 * editors — those are CONDITIONS, not weights, so they stay editable per row even
 * though the points themselves are now derived from rank. */
type UrgencySignal = {
  key: string;
  label: string;
  when: (u: any, setPath: (p: (string | number)[], v: unknown) => void) => React.ReactNode;
};

const URGENCY_SIGNALS: UrgencySignal[] = [
  { key: 'repriced', label: 'Repriced', when: (u, sp) => (
    <>days since reprice ≤ <InlineNum value={u.reprice_days} onChange={(v) => sp(['urgency', 'reprice_days'], v)} /></>) },
  { key: 'fresh', label: 'Fresh', when: (u, sp) => (
    <>listed &lt; <InlineNum value={u.fresh_days} onChange={(v) => sp(['urgency', 'fresh_days'], v)} /> days</>) },
  { key: 'aged', label: 'Aged', when: (u, sp) => (
    <>listed ≥ <InlineNum value={u.aged_days} onChange={(v) => sp(['urgency', 'aged_days'], v)} /> days</>) },
  { key: 'has_bids', label: 'Has bids', when: (u, sp) => (
    <>bids &gt; <InlineNum value={u.bids_gt} onChange={(v) => sp(['urgency', 'bids_gt'], v)} /></>) },
  { key: 'relisted', label: 'Relisted', when: (u, sp) => (
    <>listings &gt; <InlineNum value={u.listing_occ_gt} onChange={(v) => sp(['urgency', 'listing_occ_gt'], v)} /></>) },
  { key: 'under_market', label: 'At/under market', when: () => <>asking ≤ the “Priced Right” edge</> },
  { key: 'over_market', label: 'Over market', when: () => <>asking &gt; the “Over Market” edge</> },
  { key: 'velocity_fast', label: 'Fast velocity', when: (u, sp) => (
    <>avg days to sell ≤ <InlineNum value={u.velocity_fast_days} onChange={(v) => sp(['urgency', 'velocity_fast_days'], v)} /></>) },
  { key: 'velocity_slow', label: 'Slow velocity', when: (u, sp) => (
    <>avg days to sell ≥ <InlineNum value={u.velocity_slow_days} onChange={(v) => sp(['urgency', 'velocity_slow_days'], v)} /></>) },
  { key: 'supply_low', label: 'Supply: Low', when: () => <>market supply is Low</> },
  { key: 'supply_high', label: 'Supply: High', when: () => <>market supply is High</> },
  { key: 'supply_excessive', label: 'Supply: Excessive', when: () => <>market supply is Excessive</> },
  { key: 'demand_hot', label: 'Demand: Hot', when: () => <>market demand is Hot</> },
  { key: 'demand_cold', label: 'Demand: Cold', when: () => <>market demand is Cold</> },
  { key: 'coverage_high', label: 'Buyer coverage: Deep', when: (u, sp) => (
    <>buyers in cohort &gt; <InlineNum value={u.coverage_high_gt} onChange={(v) => sp(['urgency', 'coverage_high_gt'], v)} /></>) },
  { key: 'coverage_low', label: 'Buyer coverage: Thin', when: (u, sp) => (
    <>buyers in cohort &lt; <InlineNum value={u.coverage_low_lt} onChange={(v) => sp(['urgency', 'coverage_low_lt'], v)} /></>) },
];

/** §1.1.5 ONE ranked list: order sets how much a signal matters, the toggle sets
 * which way it pushes. Points are derived, so there are no 16 raw numbers to
 * reconcile — but each row still carries its condition, its live board impact and
 * the points it ended up with, so nothing is hidden. */
const RankedSignals: React.FC<{
  u: any;
  impact: Record<string, BrokerFactorImpact>;
  impactTotal: number;
  setPath: (p: (string | number)[], v: unknown) => void;
}> = ({ u, impact, impactTotal, setPath }) => {
  const rank = (pred: (w: number) => boolean) =>
    [...URGENCY_SIGNALS].filter((s) => pred(u.weights[s.key] ?? 0))
      .sort((a, b) => Math.abs(u.weights[b.key] ?? 0) - Math.abs(u.weights[a.key] ?? 0))
      .map((s) => s.key);

  // Boosts and penalties are ranked SEPARATELY: they are different budgets. The
  // boosts share 100 between them; a penalty is a deduction off that same 100 and
  // has no business competing for a slot in it.
  const [boosts, setBoosts] = useState<string[]>(() => rank((w) => w > 0));
  const [penalties, setPenalties] = useState<string[]>(() => rank((w) => w < 0));
  const [ties, setTies] = useState<Record<string, string[]>>({});
  const [err, setErr] = useState<string | null>(null);
  const dragFrom = useRef<{ group: 'boost' | 'penalty'; i: number } | null>(null);
  const byKey = Object.fromEntries(URGENCY_SIGNALS.map((s) => [s.key, s]));
  const off = URGENCY_SIGNALS.map((s) => s.key)
    .filter((k) => !boosts.includes(k) && !penalties.includes(k));
  const boostTotal = boosts.reduce((t, k) => t + Math.abs(u.weights[k] ?? 0), 0);

  const spread = (keys: string[]) => {
    const on = keys.map((k) => Math.abs(u.weights[k] ?? 0)).filter((w) => w > 0);
    return on.length > 1 ? Math.max(...on) / Math.min(...on) : 3;
  };

  const derive = async (keys: string[], dir: 'boost' | 'penalty') => {
    if (!keys.length) return;
    try {
      const dirs = Object.fromEntries(keys.map((k) => [k, dir]));
      const r = await siftApiClient.rankBrokerWeights(keys, spread(keys), dirs);
      setTies(r.ties); setErr(null);
      Object.entries(r.weights).forEach(([k, v]) => setPath(['urgency', 'weights', k], v));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not derive shares');
    }
  };

  const move = (group: 'boost' | 'penalty', from: number, to: number) => {
    const list = group === 'boost' ? boosts : penalties;
    if (to < 0 || to >= list.length || from === to) return;
    const next = list.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    (group === 'boost' ? setBoosts : setPenalties)(next);
    void derive(next, group);
  };

  const setDirection = async (key: string, dir: 'boost' | 'penalty' | 'off') => {
    const nextB = boosts.filter((k) => k !== key);
    const nextP = penalties.filter((k) => k !== key);
    if (dir === 'boost') nextB.push(key);
    if (dir === 'penalty') nextP.push(key);
    if (dir !== 'boost' && !nextB.length) { setErr('At least one boost must stay on'); return; }
    setBoosts(nextB); setPenalties(nextP);
    if (dir === 'off') setPath(['urgency', 'weights', key], 0);
    // Both budgets are re-derived: moving a signal out of the boosts frees its
    // share, which the remaining boosts have to absorb.
    await derive(nextB, 'boost');
    await derive(nextP, 'penalty');
  };

  /** Set directly. Boosts SHOULD total 100, but auto-rebalancing rewrote every other
   *  row on each keystroke, so a deliberate value never survived the next edit. The
   *  running total below reports the gap instead of silently closing it. */
  const setBoostPct = (key: string, pct: number) => {
    setErr(null);
    setPath(['urgency', 'weights', key], Math.max(1, Math.min(100, Math.round(pct))));
  };

  /** A penalty is an independent deduction, so it is simply set. */
  const setPenaltyPct = (key: string, pct: number) => {
    setErr(null);
    setPath(['urgency', 'weights', key], -Math.max(1, Math.min(100, Math.round(pct))));
  };

  const renderList = (keys: string[], group: 'boost' | 'penalty') => (
    <ol className="bss-ranked-list">
      {keys.map((k, i) => {
        const sig = byKey[k];
        const w = u.weights[k] ?? 0;
        return (
          <li key={k} className="bss-ranked-row" draggable
              onDragStart={() => { dragFrom.current = { group, i }; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const from = dragFrom.current;
                if (from && from.group === group) move(group, from.i, i);
                dragFrom.current = null;
              }}>
            <span className="bss-ranked-pos">{i + 1}</span>
            <span className="bss-ranked-body">
              <span className="bss-ranked-name">{sig.label}</span>
              <span className="bss-ranked-when">fires when {sig.when(u, setPath)}</span>
              {impact[k] && <FactorImpactLine impact={impact[k]} total={impactTotal} />}
            </span>
            <span className="bss-ranked-dir" role="group" aria-label={`${sig.label} direction`}>
              {(['boost', 'penalty', 'off'] as const).map((opt) => (
                <button key={opt} type="button"
                        className={`bss-dir-btn${group === opt ? ' bss-dir-btn-on' : ''}`}
                        aria-pressed={group === opt}
                        aria-label={`${sig.label} ${opt}`}
                        onClick={() => void setDirection(k, opt)}>
                  {opt === 'boost' ? 'Boost' : opt === 'penalty' ? 'Penalty' : 'Off'}
                </button>
              ))}
            </span>
            <span className="bss-ranked-pts">
              <span className="bss-fit-pct-wrap">
                <span className="bss-fit-pct-sign">{group === 'boost' ? '+' : '−'}</span>
                <input type="number" className="glass-input bss-fit-pct" min={1} max={100}
                       aria-label={`${sig.label} percent`}
                       value={Math.abs(w)}
                       onChange={(e) => {
                         const n = parseInt(e.target.value, 10);
                         if (Number.isNaN(n)) return;
                         if (group === 'boost') setBoostPct(k, n); else setPenaltyPct(k, n);
                       }} />
                <span className="bss-fit-pct-sign">%</span>
              </span>
            </span>
            <span className="bss-rank-move">
              <button type="button" aria-label={`Move ${sig.label} up`}
                      disabled={i === 0} onClick={() => move(group, i, i - 1)}>↑</button>
              <button type="button" aria-label={`Move ${sig.label} down`}
                      disabled={i === keys.length - 1} onClick={() => move(group, i, i + 1)}>↓</button>
            </span>
          </li>
        );
      })}
    </ol>
  );

  return (
    <div className="bss-ranked">
      {err && <div className="bss-solver-err">{err}</div>}

      <div className="bss-subhead">
        Boosts <span className="bss-legend-sub">what earns urgency — these must total 100%</span>
      </div>
      <div role="status"
           className={`bss-weight-total${boostTotal === 100 ? ' bss-weight-total-ok' : ' bss-weight-total-warn'}`}>
        Boosts total <b>{boostTotal}%</b>
        {boostTotal === 100
          ? ' — balanced.'
          : boostTotal > 100
            ? ` — that is ${boostTotal - 100}% over. Take ${boostTotal - 100}% off to reach 100%.`
            : ` — that is ${100 - boostTotal}% under. Add ${100 - boostTotal}% to reach 100%.`}
      </div>
      {renderList(boosts, 'boost')}

      <div className="bss-subhead">
        Penalties <span className="bss-legend-sub">each subtracts from the score independently</span>
      </div>
      {penalties.length
        ? renderList(penalties, 'penalty')
        : <p className="bss-help">No penalties. Switch a signal to Penalty to subtract on it.</p>}

      {off.length > 0 && (
        <>
          <div className="bss-subhead">Off <span className="bss-legend-sub">scores nothing</span></div>
          <ul className="bss-ranked-list">
            {off.map((k) => (
              <li key={k} className="bss-ranked-row bss-ranked-off">
                <span className="bss-ranked-pos">—</span>
                <span className="bss-ranked-body">
                  <span className="bss-ranked-name">{byKey[k].label}</span>
                  <span className="bss-ranked-when">fires when {byKey[k].when(u, setPath)}</span>
                </span>
                <span className="bss-ranked-dir" role="group" aria-label={`${byKey[k].label} direction`}>
                  {(['boost', 'penalty', 'off'] as const).map((opt) => (
                    <button key={opt} type="button"
                            className={`bss-dir-btn${opt === 'off' ? ' bss-dir-btn-on' : ''}`}
                            aria-pressed={opt === 'off'}
                            aria-label={`${byKey[k].label} ${opt}`}
                            onClick={() => void setDirection(k, opt)}>
                      {opt === 'boost' ? 'Boost' : opt === 'penalty' ? 'Penalty' : 'Off'}
                    </button>
                  ))}
                </span>
                <span className="bss-ranked-pts"><span className="bss-fit-zero">0%</span></span>
              </li>
            ))}
          </ul>
        </>
      )}

      {Object.keys(ties).length > 0 && (
        <div className="bss-rank-ties">
          Some positions share a share ({Object.entries(ties).map(([m, ks]) => `${ks.length} at ${m}%`).join(', ')}).
          Reordering inside one will not change any VIN's score.
        </div>
      )}
    </div>
  );
};

type NudgeKnob = {
  key: string;
  label: string;
  what: string;
  controls: (rec: any, sp: (p: (string | number)[], v: unknown) => void) => React.ReactNode;
};

/** The knobs that nudge an already-ranked list. Their sub-settings live inside
 * the row they shape, so a threshold cannot be read as a lever of its own — and
 * so the multipliers that a master switch governs sit visibly under it. */
const NUDGE_KNOBS: NudgeKnob[] = [
  { key: 'engagement_boost', label: 'Engaged this VIN',
    what: "A buyer who engaged this exact VIN (lost a bid, is bidding, made an offer, viewed/saved) gets up to this many fit points added. Only their strongest signal counts, scaled by the fractions below. 0 = off.",
    controls: (rec, sp) => (
      <>
        <div className="bss-grid">
          <NumField label="Max boost (points)" value={rec.engagement_boost ?? 8} onChange={(v) => sp(['recommend', 'engagement_boost'], v)} />
        </div>
        <details className="bss-fit-details">
          <summary>How much each kind of engagement counts</summary>
          <p className="bss-help">
            A <b>made offer</b> already outranks a plain <b>bid</b>: measured 13 Aug 2026, offers convert
            at <b>31.6%</b> vs <b>29.6%</b> for bid-only (n=79,667). Every offer in the feed also carries
            a bid, so it is a stronger flavour of bidding, not a separate action.
          </p>
          <div className="bss-grid">
            <NumField label="Lost bid" step={0.05} value={rec.engagement_salience?.lost_bid ?? 1.0} onChange={(v) => sp(['recommend', 'engagement_salience', 'lost_bid'], v)} />
            <NumField label="Bidding now" step={0.05} value={rec.engagement_salience?.already_bidding ?? 0.85} onChange={(v) => sp(['recommend', 'engagement_salience', 'already_bidding'], v)} />
            <NumField label="Made offer" step={0.05} value={rec.engagement_salience?.made_offer ?? 0.7} onChange={(v) => sp(['recommend', 'engagement_salience', 'made_offer'], v)} />
            <NumField label="Bid" step={0.05} value={rec.engagement_salience?.has_bid ?? 0.5} onChange={(v) => sp(['recommend', 'engagement_salience', 'has_bid'], v)} />
            <NumField label="Viewed / saved" step={0.05} value={rec.engagement_salience?.viewed_or_saved ?? 0.25} onChange={(v) => sp(['recommend', 'engagement_salience', 'viewed_or_saved'], v)} />
          </div>
        </details>
      </>
    ) },
  { key: 'shopping_recency_boost', label: 'Still shopping this cohort',
    what: "The recency score knows how long ago a buyer acted, not what they did — so \u201cbought last week\u201d and \u201clost a bid last week\u201d score the same, though only one is still shopping. This rewards a buyer whose latest move was an unconverted bid. 0 = off.",
    controls: (rec, sp) => (
      <div className="bss-grid">
        <NumField label="Boost (points)" step={0.5} value={rec.shopping_recency_boost ?? 0} onChange={(v) => sp(['recommend', 'shopping_recency_boost'], v)} />
      </div>
    ) },
  { key: 'recent_purchase_penalty', label: 'Just bought one',
    what: 'Discounts a buyer who purchased inside the window, tapering to zero across it. 0 = off.',
    controls: (rec, sp) => (
      <div className="bss-grid">
        <NumField label="Penalty (points)" step={0.5} value={rec.recent_purchase_penalty ?? 0} onChange={(v) => sp(['recommend', 'recent_purchase_penalty'], v)} />
        <NumField label="Window (days)" value={rec.recent_purchase_days ?? 30} onChange={(v) => sp(['recommend', 'recent_purchase_days'], v)} />
      </div>
    ) },
  { key: 'price_tol_penalty', label: 'Priced above what they pay',
    what: 'When a unit is priced above what a buyer typically pays (their historical % of MMR), they get a penalty. It reaches the maximum once the gap hits the threshold, softened when wholesale demand is hot. 0 = off.',
    controls: (rec, sp) => (
      <div className="bss-grid">
        <NumField label="Max penalty (points)" value={rec.price_tol_penalty ?? 8} onChange={(v) => sp(['recommend', 'price_tol_penalty'], v)} />
        <NumField label="Gap (fraction of MMR)" step={0.01} value={rec.price_tol_gap ?? 0.15} onChange={(v) => sp(['recommend', 'price_tol_gap'], v)} />
        <NumField label="Demand relief (0–1)" step={0.05} value={rec.price_tol_demand_relief ?? 0.6} onChange={(v) => sp(['recommend', 'price_tol_demand_relief'], v)} />
      </div>
    ) },
  { key: 'prox_value_reference', label: 'Stretch distance for valuable cars',
    what: 'Transport cost is roughly flat in dollars, so it eats a bigger share of a cheap unit. This shortens the proximity decay for low-value VINs and lengthens it for high-value ones: effective decay = decay × (MMR ÷ reference), clamped by the multipliers. Reference 0 = off, which also switches off both multipliers.',
    controls: (rec, sp) => {
      const refOff = !(Number(rec.prox_value_reference) > 0);
      return (
        <>
          <div className="bss-grid">
            <NumField label="Reference value ($, 0 = off)" value={rec.prox_value_reference ?? 0} onChange={(v) => sp(['recommend', 'prox_value_reference'], v)} />
          </div>
          <div className={refOff ? 'bss-knob-sub bss-knob-sub-off' : 'bss-knob-sub'}>
            {refOff && (
              <p className="bss-help bss-impact-inert">
                The two multipliers below do <b>nothing</b> while Reference value is 0.
              </p>
            )}
            <div className="bss-grid">
              <NumField label="Min multiplier" step={0.1} value={rec.prox_value_min_mult ?? 0.5} onChange={(v) => sp(['recommend', 'prox_value_min_mult'], v)} />
              <NumField label="Max multiplier" step={0.1} value={rec.prox_value_max_mult ?? 2.0} onChange={(v) => sp(['recommend', 'prox_value_max_mult'], v)} />
            </div>
          </div>
        </>
      );
    } },
];

/** What a knob measurably does, because its VALUE says nothing about that: two
 * knobs both set to 8 differ ~400x in effect. An OFF knob reports what it WOULD
 * do, since measuring it against its own neutral value always returns zero and
 * would read as "this control does nothing". */
const KnobImpactLine: React.FC<{ impact: BrokerRecommendKnobImpact }> = ({ impact }) => {
  const pct = (n: number) => `${(n * 100).toFixed(n < 0.01 && n > 0 ? 2 : 0)}%`;
  if (impact.state === 'off') {
    return (
      <span className="bss-impact bss-impact-off">
        <b>off</b> — switching it on would change <b>{pct(impact.moves)}</b> of recommendations
        {impact.moves_best > 0 && <> and <b>{pct(impact.moves_best)}</b> of best buyers</>}
      </span>
    );
  }
  if (impact.inert) {
    return (
      <span className="bss-impact bss-impact-inert">
        on, but changing it moves <b>nothing</b> on today&rsquo;s board
      </span>
    );
  }
  return (
    <span className="bss-impact">
      changes <b>{pct(impact.moves)}</b> of recommendations
      {impact.moves_best > 0 && <> and <b>{pct(impact.moves_best)}</b> of best buyers</>}
    </span>
  );
};

/** The six fit components, in fixed declaration order (display order comes from
 * the ranking). `what` is what the component measures, in the broker's terms;
 * `details` is the tuning that shapes only that component, which lives inside its
 * row rather than in a separate block further down the page. */
type FitComponent = {
  key: string;
  label: string;
  what: string;
  detailsLabel?: string;
  details?: (fit: any, sp: (p: (string | number)[], v: unknown) => void) => React.ReactNode;
};

const FIT_COMPONENTS: FitComponent[] = [
  { key: 'match', label: 'Match type', what: "how tightly the buyer's cohort matches the VIN (Exact → Make/Model)",
    detailsLabel: 'Tune match type — sub-scores, older cohorts, engine',
    details: (fit, sp) => (
      <>
        <p className="bss-help">Each tier scores 0–1; the Match type weight multiplies it.</p>
        <div className="bss-signals">
          <div className="bss-signal-head"><span>Tier — requires</span><span>Sub-score</span></div>
          <SignalRow name="Exact" weight={fit.tier_fit['4']} step={0.01} onWeight={(v) => sp(['fit', 'tier_fit', '4'], v)}>
            cohort + trim + drivetrain
          </SignalRow>
          <SignalRow name="Cohort" weight={fit.tier_fit['3']} step={0.01} onWeight={(v) => sp(['fit', 'tier_fit', '3'], v)}>
            age + mileage + CR cohort
          </SignalRow>
          <SignalRow name="Similar" weight={fit.tier_fit['2']} step={0.01} onWeight={(v) => sp(['fit', 'tier_fit', '2'], v)}>
            age + mileage or CR
          </SignalRow>
          <SignalRow name="Make/Model" weight={fit.tier_fit['1']} step={0.01} onWeight={(v) => sp(['fit', 'tier_fit', '1'], v)}>
            make + model only
          </SignalRow>
          <SignalRow name="Segment (sibling)" weight={fit.tier_fit['0']} step={0.01} onWeight={(v) => sp(['fit', 'tier_fit', '0'], v)}>
            same segment, same parent company — e.g. GMC Yukon ↔ Chevy Tahoe
          </SignalRow>
          <SignalRow name="Segment (cross-brand)" weight={fit.cross?.segment_fit ?? 0.22} step={0.01} onWeight={(v) => sp(['fit', 'cross', 'segment_fit'], v)}>
            same segment, different parent — e.g. Corolla Cross ↔ HR-V
          </SignalRow>
        </div>
        <div className="bss-subhead">Segment matching on older cohorts</div>
        <p className="bss-help">Outside late-model cohorts, similar vehicles often convert better than exact ones, so a Segment buyer can be lifted to compete with same-model matches. <b>Older-cohort boost</b> adds to its sub-score; <b>Rank as tier</b> is how high it may sort (0 = always below every same-model match, 4 = competes with Exact). Late-model cohorts always keep same-model dominant.</p>
        <div className="bss-grid">
          <NumField label="Older-cohort boost (0–1)" step={0.01} value={fit.cross?.older_boost ?? 0.25} onChange={(v) => sp(['fit', 'cross', 'older_boost'], v)} />
          <NumField label="Rank as tier (0–4)" step={1} value={fit.cross?.older_order_tier ?? 2} onChange={(v) => sp(['fit', 'cross', 'older_order_tier'], v)} />
        </div>
        <div className="bss-subhead">Engine match</div>
        <p className="bss-help">A soft penalty when a buyer has <b>never transacted the VIN's engine (cylinder count)</b> — e.g. a V8 buyer for a 4-cylinder truck. Null-safe (no penalty when either side's engine is unknown). Set to <b>0</b> to turn off.</p>
        <div className="bss-grid">
          <NumField label="Engine mismatch penalty (0–1)" step={0.01} value={fit.engine_penalty ?? 0.06} onChange={(v) => sp(['fit', 'engine_penalty'], v)} />
        </div>
      </>
    ) },
  { key: 'prox', label: 'Proximity', what: 'how close the buyer is to the vehicle',
    detailsLabel: 'Tune proximity — decay, unknown locations',
    details: (fit, sp) => (
      <>
        <p className="bss-help">Distance is measured from the seller (offsite listings) or the auction (onsite). A larger <b>Decay</b> keeps far-away buyers competitive (high-value cars worth shipping); a smaller one favors nearby buyers. <b>No-distance value</b> is the neutral fallback when a location is unknown.</p>
        <div className="bss-grid">
          <NumField label="Decay (miles)" value={fit.prox_decay} onChange={(v) => sp(['fit', 'prox_decay'], v)} />
          <NumField label="No-distance value (0–1)" step={0.01} value={fit.prox_null} onChange={(v) => sp(['fit', 'prox_null'], v)} />
        </div>
      </>
    ) },
  { key: 'strength', label: 'Buyer strength', what: 'how active / high-volume a buyer they are' },
  { key: 'retention', label: 'MMR retention', what: 'how close to MMR they typically pay (willingness to pay up)' },
  { key: 'recency', label: 'Recency', what: "how recently they've been transacting this cohort" },
  { key: 'engage', label: 'Engagement', what: 'their historical bid / view activity in this cohort' },
];

/** Fit weights are a SIMPLEX — they must total exactly 1.00 or the save is
 * refused. Typing six numbers that have to be rebalanced by hand is what made
 * this section unusable, so the order sets the weights and the sum is right by
 * construction.
 *
 * Two things this has to be honest about:
 *  - Moving one component moves ALL of them; the delta chips show the whole
 *    blend shifting so it doesn't look like the panel changed things unasked.
 *  - Weight is not influence. A component can carry the largest weight and still
 *    barely reorder buyers if it scores the same on all of them, so each row
 *    reports what it actually drives. */
const RankedFitWeights: React.FC<{
  fit: any;
  weights: Record<string, number>;
  impact: Record<string, BrokerFitComponentImpact>;
  onWeights: (w: Record<string, number>) => void;
  setPath: (p: (string | number)[], v: unknown) => void;
}> = ({ fit, weights, impact, onWeights, setPath }) => {
  // Order is not stored: it IS the weights, read back as descending weight.
  const [order, setOrder] = useState<string[]>(() =>
    [...FIT_COMPONENTS].filter((c) => (weights[c.key] ?? 0) > 0)
      .sort((a, b) => (weights[b.key] ?? 0) - (weights[a.key] ?? 0)).map((c) => c.key));
  const [off, setOff] = useState<string[]>(() =>
    FIT_COMPONENTS.filter((c) => (weights[c.key] ?? 0) <= 0).map((c) => c.key));
  const [deltas, setDeltas] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);
  const dragFrom = useRef<number | null>(null);
  const byKey = Object.fromEntries(FIT_COMPONENTS.map((c) => [c.key, c]));

  /** Reordering should keep the spread the admin already has, so it is read back
   * from the current weights rather than being a control of its own. Read from
   * the DRAFT, not the saved settings, or an unsaved edit silently reverts. */
  const spread = () => {
    const on = order.map((k) => weights[k] ?? 0).filter((w) => w > 0);
    return on.length > 1 ? Math.max(...on) / Math.min(...on) : 3;
  };

  const derive = async (nextOrder: string[], nextOff: string[]) => {
    try {
      const r = await siftApiClient.rankBrokerFitWeights(nextOrder, spread(), nextOff);
      setDeltas(Object.fromEntries(r.components.map((c) => [c.key, c.delta])));
      setErr(null);
      onWeights(r.weights);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not derive weights');
    }
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = order.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    setOrder(next);
    void derive(next, off);
  };

  const toggleOff = (key: string) => {
    const isOff = off.includes(key);
    // Turning one back on puts it last: it has no rank to restore.
    const nextOff = isOff ? off.filter((k) => k !== key) : [...off, key];
    const nextOrder = isOff ? [...order, key] : order.filter((k) => k !== key);
    if (nextOrder.length === 0) { setErr('At least one component must be on'); return; }
    setOff(nextOff); setOrder(nextOrder);
    void derive(nextOrder, nextOff);
  };

  /** Ranking sets the shape; this sets one share exactly. The rest rebalance
   * proportionally because the six must still total 100%. */
  const setWeight = (key: string, pct: number) => {
    const next = rebalanceFitWeights(weights, key, pct, order);
    setDeltas(Object.fromEntries(
      Object.keys(next).map((k) => [k, Math.round((next[k] - (weights[k] ?? 0)) * 100) / 100])));
    setErr(null);
    onWeights(next);
  };

  /** Re-rank from the typed values on commit, not on every keystroke — rows
   * jumping under the cursor mid-edit is worse than a stale number for a moment. */
  const commitWeights = () => {
    setOrder([...order].sort((a, b) => (weights[b] ?? 0) - (weights[a] ?? 0)));
  };

  return (
    <div className="bss-ranked">
      <div className="bss-ranked-top">
        <span>Most important at the top. Weights follow the order and always total 100% — or set a
          share directly and the rest rebalance around it.</span>
      </div>
      {err && <div className="bss-solver-err">{err}</div>}
      <ol className="bss-ranked-list">
        {[...order, ...off].map((k, idx) => {
          const c = byKey[k];
          const isOff = off.includes(k);
          const i = isOff ? -1 : idx;
          const w = weights[k] ?? 0;
          const d = deltas[k] ?? 0;
          const im = impact[k];
          return (
            <li key={k} className={`bss-ranked-row${isOff ? ' bss-ranked-off' : ''}`}
                draggable={!isOff}
                onDragStart={() => { if (!isOff) dragFrom.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragFrom.current != null && !isOff) move(dragFrom.current, i); dragFrom.current = null; }}>
              <span className="bss-ranked-pos">{isOff ? '—' : i + 1}</span>
              <span className="bss-ranked-body">
                <span className="bss-ranked-name">{c.label}</span>
                <span className="bss-ranked-when">{c.what}</span>
                {im && !isOff && <FitInfluenceLine impact={im} />}
                {c.details && (
                  <details className="bss-fit-details">
                    <summary>{c.detailsLabel}</summary>
                    {c.details(fit, setPath)}
                  </details>
                )}
              </span>
              <span className="bss-ranked-dir" role="group" aria-label={`${c.label} on or off`}>
                <button type="button" className={`bss-dir-btn${!isOff ? ' bss-dir-btn-on' : ''}`}
                        aria-pressed={!isOff} aria-label={`${c.label} on`}
                        onClick={() => { if (isOff) toggleOff(k); }}>On</button>
                <button type="button" className={`bss-dir-btn${isOff ? ' bss-dir-btn-on' : ''}`}
                        aria-pressed={isOff} aria-label={`${c.label} off`}
                        onClick={() => { if (!isOff) toggleOff(k); }}>Off</button>
              </span>
              <span className="bss-ranked-pts">
                {isOff ? (
                  <span className="bss-fit-zero">0%</span>
                ) : (
                  <span className="bss-fit-pct-wrap">
                    <input type="number" className="glass-input bss-fit-pct" min={1} max={100}
                           aria-label={`${c.label} weight percent`}
                           value={Math.round(w * 100)}
                           onChange={(e) => {
                             const n = parseInt(e.target.value, 10);
                             if (!Number.isNaN(n)) setWeight(k, n);
                           }}
                           onBlur={commitWeights} />
                    <span className="bss-fit-pct-sign">%</span>
                  </span>
                )}
                {Math.abs(d) >= 0.005 && (
                  <span className={`bss-fit-delta${d > 0 ? ' bss-fit-delta-up' : ' bss-fit-delta-down'}`}>
                    {d > 0 ? '+' : '−'}{Math.abs(Math.round(d * 100))}
                  </span>
                )}
              </span>
              <span className="bss-rank-move">
                <button type="button" aria-label={`Move ${c.label} up`}
                        disabled={isOff || i === 0} onClick={() => move(i, i - 1)}>↑</button>
                <button type="button" aria-label={`Move ${c.label} down`}
                        disabled={isOff || i === order.length - 1} onClick={() => move(i, i + 1)}>↓</button>
              </span>
            </li>
          );
        })}
      </ol>
      <div className="bss-fit-note">
        These six shares always add to 100%, so raising one lowers the others — the numbers on the
        right show every component that moved.
      </div>
    </div>
  );
};

/** Weight is what a component was asked to do; this is what it actually does.
 * Called out separately because the two can disagree sharply — "Match type"
 * carries the largest weight and drives the least separation, because every
 * buyer in the list matched by construction. */
const FitInfluenceLine: React.FC<{ impact: BrokerFitComponentImpact }> = ({ impact }) => {
  const pct = (impact.share_of_spread * 100).toFixed(0);
  if (impact.low_separation) {
    return (
      <span className="bss-impact bss-impact-inert">
        drives only <b>{pct}%</b> of how buyers get ordered, despite {Math.round(impact.weight * 100)}% weight —
        it scores about the same ({impact.mean.toFixed(2)}) on nearly every matched buyer, so raising it
        changes little
      </span>
    );
  }
  return (
    <span className="bss-impact">
      drives <b>{pct}%</b> of how buyers actually get ordered
    </span>
  );
};

/** §1.1.4: state the outcome you want and get the cutoff that produces it.
 *
 * Asked as a SHARE OF THE BOARD, because that is the only form of the question an
 * admin can answer — a bare threshold has no referent, which is how the live one
 * ended up selecting 15 vehicles. The score is coarse (~35 distinct values) so many
 * shares are unreachable; the backend names the two nearest rather than silently
 * missing. Applies to the DRAFT only, so it still goes through preview and Save. */
const CutoffSolver: React.FC<{ onApply: (tier: string, cutoff: number) => void }> = ({ onApply }) => {
  const [tier, setTier] = useState('critical');
  const [share, setShare] = useState(1);
  const [result, setResult] = useState<BrokerCutoffSolution | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const solve = async () => {
    setBusy(true); setErr(null); setResult(null);
    try {
      setResult(await siftApiClient.solveBrokerTierCutoffByShare(tier, share / 100));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not solve for that target');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bss-solver">
      <div className="bss-subhead">Or state the outcome you want</div>
      <p className="bss-help">
        Say what share of the board a tier should be and this works out the cutoff.
        It fills in the draft above — nothing is saved until you press Save.
      </p>
      <div className="bss-solver-row">
        <label>I want
          <select className="glass-input" value={tier} onChange={(e) => setTier(e.target.value)} aria-label="Tier to solve">
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
          </select>
        </label>
        <label>to be about
          <input type="number" className="glass-input bss-num" value={share} min={0.1} step={0.5}
                 aria-label="Target share of the board"
                 onChange={(e) => setShare(parseFloat(e.target.value) || 0)} />
        </label>
        <span>% of the board</span>
        <button type="button" className="glass-button" onClick={solve} disabled={busy}>
          {busy ? 'Working…' : 'Work out the cutoff'}
        </button>
      </div>
      {err && <div className="bss-solver-err">{err}</div>}
      {result && (
        <div className={result.achievable ? 'bss-solver-ok' : 'bss-solver-gap'}>
          {result.achievable ? (
            <>A cutoff of <b>{result.cutoff}%</b> puts <b>{result.resulting_count.toLocaleString()}</b> vehicles
              in {result.tier} — {((result.resulting_share ?? 0) * 100).toFixed(2)}% of the board.</>
          ) : (
            <>
              <b>No cutoff hits that share.</b> Urgency takes only a few dozen distinct
              values, so the reachable shares jump; the closest are{' '}
              {result.nearest_below && <>{result.nearest_below.count.toLocaleString()} (cutoff {result.nearest_below.cutoff}%)</>}
              {result.nearest_below && result.nearest_above && ' and '}
              {result.nearest_above && <>{result.nearest_above.count.toLocaleString()} (cutoff {result.nearest_above.cutoff}%)</>}
              . Closest is <b>{result.cutoff}%</b> → {result.resulting_count.toLocaleString()}.
            </>
          )}
          {result.breaks_tier_order && (
            <div className="bss-solver-err">
              This would put the tiers out of order (Critical ≥ High ≥ Medium) and Save would reject it.
            </div>
          )}
          <button type="button" className="glass-button bss-solver-apply"
                  onClick={() => onApply(result.tier, result.cutoff)}>
            Use cutoff {result.cutoff}
          </button>
        </div>
      )}
    </div>
  );
};

/** §1.1.3: what this control is doing to today's board, so the number above it
 * has a real-world referent. "Cannot fire" is called out separately from "scored
 * zero" — a weighted control with no input is the thing that makes tuning feel
 * unpredictable. */
const FactorImpactLine: React.FC<{ impact: BrokerFactorImpact; total: number }> = ({ impact, total }) => {
  const pct = total ? (impact.vins_affected / total) * 100 : 0;
  const missingPct = total ? (impact.vins_unavailable / total) * 100 : 0;
  return (
    <div className="bss-impact">
      {impact.inert ? (
        <span className="bss-impact-inert">Currently affects no vehicles</span>
      ) : (
        <>
          Affects <b>{impact.vins_affected.toLocaleString()}</b> of {total.toLocaleString()} vehicles ({pct.toFixed(1)}%)
          {' · '}{impact.total_points >= 0 ? 'adds' : 'removes'}{' '}
          {Math.abs(impact.total_points).toLocaleString()} pts across the board
        </>
      )}
      {impact.vins_unavailable > 0 && (
        <span className="bss-impact-missing" title="These vehicles have no value for this input, so this control can never fire on them however it is weighted.">
          {' '}· no data on {impact.vins_unavailable.toLocaleString()} ({missingPct.toFixed(0)}%)
        </span>
      )}
    </div>
  );
};

const MIN_SEARCH_LEN = 2;
const SEARCH_DEBOUNCE_MS = 450;

/** §3.2 team-entered exceptions to the derived contact picture.
 *
 *  Deliberately NOT part of the settings draft. Everything else on this page is
 *  edit-then-Save; a DO NOT CONTACT has to take effect when it is entered, not
 *  when someone remembers to press Save, so these write immediately and the
 *  heading says so. */
const OVERRIDE_TYPES: { value: string; label: string; hint: string }[] = [
  { value: 'suppress', label: 'Do not contact',
    hint: 'Leave the address blank to cover the whole channel.' },
  { value: 'invalid', label: 'Bad address',
    hint: 'Bounced or wrong department. Needs the address.' },
  { value: 'prefer', label: 'Use this instead',
    hint: 'Wins over anything derived. Needs the address.' },
];

const ContactOverrides: React.FC = () => {
  const [rows, setRows] = useState<BrokerContactOverride[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<BrokerBuyerSearchResult[]>([]);
  const [buyer, setBuyer] = useState<BrokerBuyerSearchResult | null>(null);
  const [kind, setKind] = useState('suppress');
  const [channel, setChannel] = useState('email');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');

  const load = async () => {
    try {
      setRows(await siftApiClient.listBrokerContactOverrides());
      setErr(null);
    } catch {
      setErr('Could not load contact overrides.');
    }
  };
  useEffect(() => { void load(); }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2 || buyer) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { setResults(await siftApiClient.searchBrokerBuyers(term, 8)); } catch { /* search is additive */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, buyer]);

  // Only 'suppress' means anything without an address; the backend rejects the
  // others, so the button is disabled rather than letting it 422.
  const needsValue = kind !== 'suppress';
  const canAdd = !!buyer && (!needsValue || !!value.trim()) && !busy;

  const add = async () => {
    if (!buyer) return;
    setBusy(true);
    try {
      await siftApiClient.setBrokerContactOverride(buyer.buyer_uid, {
        channel, override_type: kind,
        contact_value: value.trim() || null,
        reason: reason.trim() || null,
      });
      setBuyer(null); setQ(''); setValue(''); setReason('');
      await load();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setErr(detail || 'Could not save that override.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (r: BrokerContactOverride) => {
    setBusy(true);
    try {
      await siftApiClient.revokeBrokerContactOverride(r.buyer_uid, r.channel, r.contact_value ?? null);
      await load();
    } catch {
      setErr('Could not revoke that override.');
    } finally {
      setBusy(false);
    }
  };

  const activeHint = OVERRIDE_TYPES.find((t) => t.value === kind)?.hint;

  return (
    <Section
      title={`Contact overrides (${rows.length})`}
      sub="applies immediately, not on Save"
      className="bss-group-overflow"
    >
      <p className="bss-help">
        Exceptions to the address SIFT picks for a buyer. These apply
        <b> as soon as you add them</b> — they are not part of Save.
      </p>
      {err && <div className="bss-solver-err">{err}</div>}

      {/* Step 1 — who. Same search affordance as Buyer exclusions, so the two
          lists in this panel do not behave differently for no reason. */}
      <div className="bss-ov-step">
        <span className="bss-ov-step-n">1</span>
        <div className="bss-ov-step-body">
          {buyer ? (
            <span className="bss-excl-chip">
              <span className="bss-buyer-name">{buyer.buyer_name || `Buyer ${buyer.buyer_uid}`}</span>
              <span className="bss-buyer-meta">#{buyer.buyer_uid}</span>
              <button type="button" aria-label="Clear buyer"
                      onClick={() => { setBuyer(null); setQ(''); }}>✕</button>
            </span>
          ) : (
            <div className="bss-search-wrap">
              <input type="text" className="glass-input bss-search" value={q}
                     role="combobox" aria-expanded={results.length > 0} aria-autocomplete="list"
                     placeholder="Search buyer name or ID…" aria-label="Search buyer"
                     onChange={(e) => setQ(e.target.value)} />
              {q.trim().length >= 2 && (
                <div className="bss-search-results">
                  {results.length === 0
                    ? <div className="bss-search-empty">No matches</div>
                    : results.map((b) => (
                        <button key={b.buyer_uid} type="button" className="bss-search-item"
                                onClick={() => { setBuyer(b); setResults([]); }}>
                          <span className="bss-buyer-name">{b.buyer_name || `Buyer ${b.buyer_uid}`}</span>
                          <span className="bss-buyer-meta">
                            #{b.buyer_uid}{b.total_buys != null ? ` · ${b.total_buys.toLocaleString()} buys` : ''}
                          </span>
                        </button>
                      ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Step 2 — what. Disabled until a buyer is chosen so the order is obvious. */}
      <div className={`bss-ov-step${buyer ? '' : ' bss-ov-step-off'}`}>
        <span className="bss-ov-step-n">2</span>
        <div className="bss-ov-step-body">
          <div className="bss-ov-rules" role="radiogroup" aria-label="Override type">
            {OVERRIDE_TYPES.map((t) => (
              <button key={t.value} type="button" role="radio" aria-checked={kind === t.value}
                      disabled={!buyer}
                      className={`bss-chip${kind === t.value ? ' bss-chip-on' : ''}`}
                      onClick={() => setKind(t.value)}>{t.label}</button>
            ))}
          </div>
          {activeHint && <p className="bss-ov-hint">{activeHint}</p>}
          <div className="bss-grid">
            <label className="bss-field">
              <span>Channel</span>
              <select className="glass-input" value={channel} aria-label="Channel" disabled={!buyer}
                      onChange={(e) => setChannel(e.target.value)}>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </label>
            <label className="bss-field">
              <span>{channel === 'phone' ? 'Number' : 'Address'}{needsValue ? '' : ' (optional)'}</span>
              <input className="glass-input" value={value} aria-label="Contact value" disabled={!buyer}
                     placeholder={needsValue ? 'required for this rule' : 'blank = the whole channel'}
                     onChange={(e) => setValue(e.target.value)} />
            </label>
            <label className="bss-field">
              <span>Reason</span>
              <input className="glass-input" value={reason} aria-label="Reason" disabled={!buyer}
                     placeholder="why the team made this call"
                     onChange={(e) => setReason(e.target.value)} />
            </label>
          </div>
          <button type="button" className="bss-ov-add" disabled={!canAdd} onClick={() => void add()}>
            {busy ? 'Saving…' : 'Add override'}
          </button>
        </div>
      </div>

      {rows.length > 0 ? (
        <ul className="bss-excl-list">
          {rows.map((r, i) => (
            <li className="bss-excl-item" key={`${r.buyer_uid}-${r.channel}-${r.contact_value || 'all'}-${i}`}>
              <span className={`bss-ov-tag bss-ov-tag-${r.override_type}`}>
                {OVERRIDE_TYPES.find((t) => t.value === r.override_type)?.label || r.override_type}
              </span>
              <span className="bss-buyer-name">
                {r.contact_value || 'whole channel'}
                <span className="bss-legend-sub"> · {r.channel} · #{r.buyer_uid}</span>
              </span>
              <span className="bss-buyer-meta">
                {[r.reason, r.created_by].filter(Boolean).join(' · ')}
              </span>
              <button type="button" className="bss-excl-remove"
                      aria-label={`Revoke override for ${r.buyer_uid}`}
                      disabled={busy} onClick={() => void revoke(r)}>✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="bss-empty">No overrides. SIFT uses the best address it can find for every buyer.</p>
      )}
    </Section>
  );
};

const BuyerExclusions: React.FC<{
  exclusions: BrokerSettings['buyer_exclusions'];
  onChange: (next: BrokerSettings['buyer_exclusions']) => void;
}> = ({ exclusions, onChange }) => {
  const [mode, setMode] = useState<'buyer' | 'parent'>('buyer');
  const [q, setQ] = useState('');
  const [buyerResults, setBuyerResults] = useState<BrokerBuyerSearchResult[]>([]);
  const [parentResults, setParentResults] = useState<BrokerParentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const excludedUids = useMemo(
    () => new Set(exclusions.map((e) => e.buyer_uid).filter(Boolean) as string[]),
    [exclusions],
  );
  const excludedParents = useMemo(
    () => new Set(exclusions.map((e) => e.buyer_parent_name).filter(Boolean) as string[]),
    [exclusions],
  );
  const trimmed = q.trim();
  const [searchError, setSearchError] = useState<string | null>(null);

  // Debounced server search — switches source by mode (buyers vs parent groups).
  useEffect(() => {
    if (trimmed.length < MIN_SEARCH_LEN) { setBuyerResults([]); setParentResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        if (mode === 'parent') {
          setParentResults(await siftApiClient.searchBrokerParents(trimmed, 15, controller.signal));
        } else {
          setBuyerResults(await siftApiClient.searchBrokerBuyers(trimmed, 15, controller.signal));
        }
        setSearchError(null);
      } catch {
        // An aborted request is expected (the user kept typing); a real failure
        // must not masquerade as "No matches", which is indistinguishable from
        // a genuinely empty result.
        if (!controller.signal.aborted) setSearchError('Search failed — try again.');
      } finally {
        setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [trimmed, mode]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const addBuyer = (b: BrokerBuyerSearchResult) => {
    if (excludedUids.has(b.buyer_uid)) return;
    onChange([...exclusions, { buyer_uid: b.buyer_uid, buyer_name: b.buyer_name }]);
    setQ(''); setBuyerResults([]); setOpen(false);
  };
  const addParent = (p: BrokerParentSearchResult) => {
    if (excludedParents.has(p.buyer_parent_name)) return;
    onChange([...exclusions, { buyer_parent_name: p.buyer_parent_name }]);
    setQ(''); setParentResults([]); setOpen(false);
  };
  const remove = (e: BrokerSettings['buyer_exclusions'][number]) =>
    onChange(exclusions.filter((x) => !(x.buyer_uid === e.buyer_uid && x.buyer_parent_name === e.buyer_parent_name)));

  const switchMode = (m: 'buyer' | 'parent') => {
    setMode(m); setQ(''); setBuyerResults([]); setParentResults([]); setSearchError(null);
  };

  return (
    <Section
      title={`Buyer exclusions (${exclusions.length})`}
      sub={exclusions.length ? 'hidden from every recommendation' : 'none yet'}
      className="bss-group-overflow"
    >
      <p className="bss-help">Buyers here are hidden from all recommendations across every tab (a recompute backfills the next-best buyer).</p>
      <div className="bss-excl-mode-row">
        <span className="bss-excl-mode-label">Exclude:</span>
        <div className="bss-excl-modes" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'buyer'}
            className={`bss-chip${mode === 'buyer' ? ' bss-chip-on' : ''}`} onClick={() => switchMode('buyer')}>One buyer</button>
          <button type="button" role="tab" aria-selected={mode === 'parent'}
            className={`bss-chip${mode === 'parent' ? ' bss-chip-on' : ''}`} onClick={() => switchMode('parent')}>A whole parent group</button>
        </div>
      </div>
      <p className="bss-excl-mode-hint">
        {mode === 'parent'
          ? 'Suppresses every sub-account rolling up to the selected group.'
          : 'Suppresses just this one buyer account.'}
      </p>
      <div className="bss-search-wrap" ref={wrapRef}>
        <input
          type="text"
          className="glass-input bss-search"
          placeholder={mode === 'parent' ? 'Search parent group name or ID to exclude…' : 'Search buyer name or ID to exclude…'}
          value={q}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        />
        {open && (
          <div className="bss-search-results">
            {trimmed.length < MIN_SEARCH_LEN
              ? <div className="bss-search-empty">Type at least {MIN_SEARCH_LEN} characters…</div>
              : searching
                ? <div className="bss-search-empty">Searching…</div>
                : searchError
                  ? <div className="bss-search-empty bss-search-error">{searchError}</div>
                  : mode === 'parent'
                  ? (parentResults.length === 0
                      ? <div className="bss-search-empty">No matches</div>
                      : parentResults.map((p) => (
                          <button
                            key={p.buyer_parent_name}
                            type="button"
                            className="bss-search-item"
                            onClick={() => addParent(p)}
                            disabled={excludedParents.has(p.buyer_parent_name)}
                          >
                            <span className="bss-buyer-name">{p.buyer_parent_name}</span>
                            <span className="bss-buyer-meta">{p.sub_accounts.toLocaleString()} sub-accounts{p.total_buys != null ? ` · ${p.total_buys.toLocaleString()} buys` : ''}{excludedParents.has(p.buyer_parent_name) ? ' · excluded' : ''}</span>
                          </button>
                        )))
                  : (buyerResults.length === 0
                      ? <div className="bss-search-empty">No matches</div>
                      : buyerResults.map((b) => (
                          <button
                            key={b.buyer_uid}
                            type="button"
                            className="bss-search-item"
                            onClick={() => addBuyer(b)}
                            disabled={excludedUids.has(b.buyer_uid)}
                          >
                            <span className="bss-buyer-name">{b.buyer_name || `Buyer ${b.buyer_uid}`}</span>
                            <span className="bss-buyer-meta">#{b.buyer_uid}{b.total_buys != null ? ` · ${b.total_buys.toLocaleString()} buys` : ''}{excludedUids.has(b.buyer_uid) ? ' · excluded' : ''}</span>
                          </button>
                        )))}
          </div>
        )}
      </div>
      {exclusions.length > 0 ? (
        <ul className="bss-excl-list">
          {exclusions.map((e, i) => (
            <li key={e.buyer_uid ? `u:${e.buyer_uid}` : `p:${e.buyer_parent_name}:${i}`} className="bss-excl-item">
              {e.buyer_parent_name ? (
                <>
                  <span className="bss-buyer-name">{e.buyer_parent_name}</span>
                  <span className="bss-buyer-meta">Parent group</span>
                </>
              ) : (
                <>
                  <span className="bss-buyer-name">{e.buyer_name || `Buyer ${e.buyer_uid}`}</span>
                  <span className="bss-buyer-meta">#{e.buyer_uid}</span>
                </>
              )}
              <button type="button" className="bss-excl-remove" onClick={() => remove(e)} aria-label={`Remove ${e.buyer_uid || e.buyer_parent_name}`}>✕</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="bss-empty">No buyers excluded. Search above to add one.</p>
      )}
    </Section>
  );
};
